import { useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { useDeleteAlertRule, useAlertRules, useProjects } from "../../api/queries";
import type { AlertRule } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { StatePill } from "../../components/StatePill";
import { useToast } from "../../toast/ToastContext";
import { AlertInstancesModal } from "./AlertInstancesModal";
import { CONDITION_META } from "./conditionMeta";
import { CreateAlertRuleModal } from "./CreateAlertRuleModal";
import { DeleteAlertRuleDialog } from "./DeleteAlertRuleDialog";
import { EditAlertRuleModal } from "./EditAlertRuleModal";
import "./alerts.css";

const WRITE_ONLY_NOTE =
  "A rule's webhook signing secret is write-only — sealed on write and never shown again. Set a new one to replace it.";

function thresholdSummary(rule: AlertRule): string {
  const meta = CONDITION_META[rule.condition];
  if (!meta.thresholdLabel) return "—";
  return rule.condition === "cert_expiry" ? `${rule.threshold}d` : `${rule.threshold}s`;
}

export function AlertsPage() {
  const { session } = useAuth();
  const { pushToast } = useToast();
  const paginator = usePaginator();
  const { data, isPending, isError, error, refetch } = useAlertRules({
    cursor: paginator.cursor,
  });
  // Feeds the "Target" column's project-name lookup — the same list the
  // create/edit modals' scope select uses.
  const { data: projectsPage } = useProjects({ limit: 100 });
  const deleteMutation = useDeleteAlertRule();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AlertRule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AlertRule | null>(null);
  const [activityTarget, setActivityTarget] = useState<AlertRule | null>(null);

  // Mirrors the server's authz matrix: alert:create/update/delete are
  // admin+operator, viewer view-only (server/internal/authz/model.go) — same
  // shape as placement/connection CRUD. UX only (S-8); the server enforces
  // the real decision on every request regardless of what this check says.
  const canManage = canManageFleet(session?.role);

  const projectName = (id: string | undefined) =>
    id ? (projectsPage?.items.find((p) => p.id === id)?.name ?? id) : "Whole fleet";

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const { id, name } = deleteTarget;
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          pushToast({ title: `Deleted "${name}".`, tone: "success" });
          setDeleteTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't delete "${name}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const newRuleButton = (
    <Button variant="primary" onClick={() => setCreateOpen(true)}>
      New alert rule
    </Button>
  );

  const columns: DataTableColumn<AlertRule>[] = [
    { key: "name", header: "Name", cell: (r) => r.name, className: "data-table__primary" },
    { key: "condition", header: "Fires when", cell: (r) => CONDITION_META[r.condition].label },
    { key: "threshold", header: "Threshold", cell: thresholdSummary },
    { key: "target", header: "Target", cell: (r) => projectName(r.scope) },
    {
      key: "webhook",
      header: "Webhook",
      cell: (r) =>
        r.webhookUrl ? (
          <StatePill label="Configured" tone="positive" shape="dot" />
        ) : (
          <StatePill label="Not configured" tone="neutral" shape="ring" />
        ),
    },
    {
      key: "enabled",
      header: "Status",
      cell: (r) =>
        r.enabled ? (
          <StatePill label="Enabled" tone="positive" shape="dot" />
        ) : (
          <StatePill label="Disabled" tone="neutral" shape="ring" />
        ),
    },
    {
      key: "actions",
      header: <span className="visually-hidden">Actions</span>,
      cell: (r) => (
        <div className="alerts-table__actions">
          <Button variant="secondary" onClick={() => setActivityTarget(r)}>
            Activity
          </Button>
          {canManage && (
            <>
              <Button variant="secondary" onClick={() => setEditTarget(r)}>
                Edit
              </Button>
              <Button variant="danger" onClick={() => setDeleteTarget(r)}>
                Delete
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Alerts"
        description="Fleet-health alert rules — a symptom-based condition, an optional webhook, and the delivery history."
        actions={canManage ? newRuleButton : undefined}
      />

      <p className="alerts-note">{WRITE_ONLY_NOTE}</p>

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading alert rules…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error
            ? error.message
            : "Couldn't load alert rules. Check your connection and try again."
        }
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No alert rules yet"
            description="Create a rule to get notified when a service fails to run somewhere, a machine goes offline, a required connection breaks, or a certificate is about to expire."
            action={canManage ? newRuleButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(r) => r.id} />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="alert rules"
            />
          </>
        )}
      </QueryStates>

      <AlertInstancesModal
        rule={activityTarget}
        onClose={() => setActivityTarget(null)}
      />

      {canManage && (
        <>
          <CreateAlertRuleModal
            open={createOpen}
            onClose={() => setCreateOpen(false)}
          />
          <EditAlertRuleModal
            rule={editTarget}
            onClose={() => setEditTarget(null)}
          />
          <DeleteAlertRuleDialog
            rule={deleteTarget}
            isDeleting={deleteMutation.isPending}
            onCancel={() => setDeleteTarget(null)}
            onConfirm={handleDeleteConfirm}
          />
        </>
      )}
    </>
  );
}
